import { useState, useCallback } from 'react';
import { useDynamiaERP } from './useDynamiaERP';

export function useSalesReport() {
    const { fetchUnifiedReport, loading: dynamiaLoading, error: dynamiaError, clearData } = useDynamiaERP();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getSalesReport = useCallback(async (from, to) => {
        setLoading(true);
        setError(null);
        console.log('[useSalesReport] Fetching report for', from, to);

        try {
            const result = await fetchUnifiedReport(from, to);

            if (result.success && result.data) {
                const raw = result.data;

                // Collect all orders — prefer the top-level orders array from the Cloud Function
                let allOrders = Array.isArray(raw.orders) && raw.orders.length > 0
                    ? raw.orders
                    : Object.values(raw.sellers || {}).flatMap(s => s.orderDetails || []);

                // Normalize every order to a consistent shape all components can rely on
                const orders = allOrders.map(order => {
                    const subtotal = Number(order.subtotal ?? order.netSales ?? 0);
                    const total    = Number(order.total ?? subtotal);
                    const tax      = order.tax !== undefined
                        ? Number(order.tax)
                        : Math.max(0, total - subtotal);
                    const items = order.items || order.products || [];

                    return {
                        ...order,
                        // Revenue fields — always use subtotal as netSales (pre-tax)
                        netSales:   subtotal,
                        subtotal:   subtotal,
                        total:      total,
                        tax:        tax,
                        // Date — try all known field names, store as ISO string
                        date:       order.date || order.orderDate || order.creationDate || '',
                        orderDate:  order.orderDate || order.date || order.creationDate || '',
                        // Seller
                        sellerName: order.sellerName || order.seller || 'Sin Asignar',
                        seller:     order.seller     || order.sellerName || 'Sin Asignar',
                        // Branch
                        branch:     order.branch || 'Sucursal Principal',
                        // Products
                        items:      items,
                        products:   items,
                    };
                });

                const mappedReport = {
                    totalNetSales:     raw.totalSales,
                    totalOrders:       raw.totalOrders,
                    totalCommissions:  raw.totalCommissions,
                    sellers:           Object.entries(raw.sellers || {}).map(([name, data]) => ({
                        sellerName:    name,
                        totalNetSales: data.sales,
                        totalOrders:   data.orders,
                        commission:    data.commission,
                        commissionRate: data.commissionRate,
                    })),
                    orders,
                    products: [],
                };

                const totalProducts = orders.reduce((s, o) => s + o.items.length, 0);
                console.log(`[useSalesReport] Mapped ${orders.length} orders with ${totalProducts} total product items`);
                if (orders.length > 0) {
                    console.log(`[useSalesReport] First order sample:`, {
                        orderNumber: orders[0].orderNumber,
                        itemsCount:  orders[0].items.length,
                        firstItem:   orders[0].items[0],
                    });
                }

                setReport(mappedReport);
                return mappedReport;
            } else {
                setError(result.error || 'Error desconocido al obtener el reporte');
                return null;
            }
        } catch (err) {
            setError(err.message || 'Error inesperado');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchUnifiedReport]);

    const clearReport = useCallback(() => {
        clearData();
        setReport(null);
    }, [clearData]);

    return {
        report,
        loading: loading || dynamiaLoading,
        error:   error   || dynamiaError,
        getSalesReport,
        clearReport,
        clearError: () => setError(null),
    };
}
